import { NameFieldsWrapper } from '../CustomerCard.styles';
import { PhoneInput } from '@interstate/components/PhoneInput';
import { TextInput } from '@interstate/components/TextInput';
import { EMPLOYMENT_STATUS_OPTIONS, EXPECTED_INCOME_FIELD, PAY_FREQUENCY_FIELD, PAY_FREQUENCY_OPTIONS } from '../constants';
import { NumericInput } from '@interstate/components/NumericInput';
import { SelectInput } from '@interstate/components/SelectInput';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { useCustomerCardContext } from '../CustomerCardProvider';
import { DurationField } from './DurationField';
import type { ErrorState } from '@makemydeal/dr-dash-ui-utils';
import { useMemo } from 'react';
import { kebabCase } from 'lodash';
import { StateCodes, getStateCodesSelectInputValues } from '@makemydeal/dr-shared-ui-utils';
import { EmployerAddressFieldWrapper } from './HousingAndEmploymentInfo.style';

type CommonEmploymentFieldsProps = {
    employmentType: string;
};

export const EmploymentFields = ({ employmentType }: CommonEmploymentFieldsProps) => {
    const testIdPrefix = kebabCase(employmentType);
    const { formValues, handleInputChange, handleNumberInputChange, isLocked, customerFormErrors } = useCustomerCardContext();
    const employmentData = formValues[employmentType as keyof typeof formValues] as Record<string, unknown>;
    const addressData = (employmentData?.address as Record<string, unknown>) || {};
    const financialInfoData = formValues.financialInfo;

    const employmentErrors = customerFormErrors?.[employmentType as keyof typeof customerFormErrors] as
        | Record<string, ErrorState>
        | undefined;

    const addressErrors = (employmentErrors?.address as unknown as Record<string, ErrorState>) || {};

    const employmentFormErrors: {
        employerName: ErrorState;
        occupation: ErrorState;
        totalYearsEmployed: ErrorState;
        totalMonthsEmployed: ErrorState;
        streetAddress: ErrorState;
        city: ErrorState;
        state: ErrorState;
        zip: ErrorState;
    } = useMemo(
        () => ({
            employerName: employmentErrors?.employerName ?? { hasError: false, errorMessage: '' },
            occupation: employmentErrors?.occupation ?? { hasError: false, errorMessage: '' },
            totalYearsEmployed: employmentErrors?.totalYearsEmployed ?? { hasError: false, errorMessage: '' },
            totalMonthsEmployed: employmentErrors?.totalMonthsEmployed ?? { hasError: false, errorMessage: '' },
            streetAddress: addressErrors.streetAddress ?? { hasError: false, errorMessage: '' },
            city: addressErrors.city ?? { hasError: false, errorMessage: '' },
            state: addressErrors.state ?? { hasError: false, errorMessage: '' },
            zip: addressErrors.zip ?? { hasError: false, errorMessage: '' }
        }),
        [customerFormErrors]
    );

    const financialInfoFormErrors: { income: ErrorState } = useMemo(
        () => ({
            income: (customerFormErrors?.financialInfo as Record<string, ErrorState>)?.income ?? {
                hasError: false,
                errorMessage: ''
            }
        }),
        [customerFormErrors]
    );

    const renderEmploymentStatus = () => {
        return (
            <SelectInput
                data-testid={`customer-form-${testIdPrefix}-status`}
                label="Employment Status"
                name="status"
                onChange={(e) => handleInputChange(`${employmentType}.status`, e.target.value || '')}
                options={EMPLOYMENT_STATUS_OPTIONS}
                value={String(employmentData?.status ?? '')}
                displayDeselectOption={true}
                size="sm"
                disabled={isLocked}
            />
        );
    };

    const renderEmployerNameField = () => {
        return (
            <TextInput
                name="employerName"
                displayLabel={true}
                label="Employer Name"
                value={String(employmentData?.employerName ?? '')}
                onChange={(e) => handleInputChange(`${employmentType}.employerName`, e.target.value)}
                hasError={Boolean(employmentFormErrors.employerName.hasError)}
                errorMessage={employmentFormErrors.employerName.errorMessage || ''}
                data-testid={`customer-form-${testIdPrefix}-employer-name`}
                size="small"
                disabled={isLocked}
            />
        );
    };

    const renderOccupationField = () => {
        return (
            <TextInput
                name="occupation"
                displayLabel={true}
                label="Occupation"
                value={String(employmentData?.occupation ?? '')}
                onChange={(e) => handleInputChange(`${employmentType}.occupation`, e.target.value)}
                hasError={Boolean(employmentFormErrors.occupation.hasError)}
                errorMessage={employmentFormErrors.occupation.errorMessage || ''}
                data-testid={`customer-form-${testIdPrefix}-occupation`}
                size="small"
                disabled={isLocked}
            />
        );
    };

    const renderEmployerPhoneField = () => {
        return (
            <PhoneInput
                name="employerPhone"
                label="Employer Phone"
                onChange={(e: InterstateOnChangeEvent<string>) => handleInputChange(`${employmentType}.workPhone`, e.target.value)}
                value={String(employmentData?.workPhone ?? '')}
                data-testid={`customer-form-${testIdPrefix}-employer-phone`}
                size="sm"
                displayLabel={true}
                disabled={isLocked}
            />
        );
    };

    const renderPayFrequencyField = () => {
        return (
            <SelectInput
                data-testid={`customer-form-${testIdPrefix}-pay-frequency`}
                label="Pay Frequency"
                name="payFrequency"
                onChange={(e) => handleInputChange(PAY_FREQUENCY_FIELD, e.target.value || '')}
                options={PAY_FREQUENCY_OPTIONS}
                value={String(financialInfoData?.incomeFrequency ?? '')}
                displayDeselectOption={true}
                size="sm"
                disabled={isLocked}
            />
        );
    };

    const renderExpectedIncomeField = () => {
        return (
            <NumericInput
                inputPrefix={'$'}
                autoInsertCommas
                allowDecimal
                decimalMaxLength={2}
                decimalMinLength={0}
                data-testid={`customer-form-${testIdPrefix}-expected-income`}
                label="Expected Income"
                name="expectedIncome"
                onChange={(e: InterstateOnChangeEvent<string>) => handleNumberInputChange(EXPECTED_INCOME_FIELD, e.target.value)}
                value={String(financialInfoData?.income ?? '')}
                hasError={Boolean(financialInfoFormErrors.income.hasError)}
                errorMessage={financialInfoFormErrors.income.errorMessage || ''}
                disabled={isLocked}
                size="sm"
            />
        );
    };

    const stateSelectInputValues = useMemo(getStateCodesSelectInputValues, StateCodes);

    const renderEmployerAddressField = () => {
        return (
            <EmployerAddressFieldWrapper>
                <TextInput
                    name="employerAddress"
                    displayLabel={true}
                    label="Employer Address"
                    value={String(addressData.streetAddress ?? '')}
                    onChange={(e) => handleInputChange(`${employmentType}.address.streetAddress`, e.target.value)}
                    hasError={Boolean(employmentFormErrors.streetAddress.hasError)}
                    errorMessage={employmentFormErrors.streetAddress.errorMessage || ''}
                    data-testid={`customer-form-${testIdPrefix}-employer-address`}
                    size="small"
                    disabled={isLocked}
                />
            </EmployerAddressFieldWrapper>
        );
    };

    const renderEmployerCityField = () => {
        return (
            <TextInput
                name="employerCity"
                displayLabel={true}
                label="City"
                value={String(addressData.city ?? '')}
                onChange={(e) => handleInputChange(`${employmentType}.address.city`, e.target.value)}
                hasError={Boolean(employmentFormErrors.city.hasError)}
                errorMessage={employmentFormErrors.city.errorMessage || ''}
                data-testid={`customer-form-${testIdPrefix}-employer-city`}
                size="small"
                disabled={isLocked}
            />
        );
    };

    const renderEmployerStateField = () => {
        return (
            <SelectInput
                data-testid={`customer-form-${testIdPrefix}-employer-state`}
                label="State"
                name="employerState"
                onChange={(e) => handleInputChange(`${employmentType}.address.state`, e.target.value || '')}
                options={stateSelectInputValues}
                value={String(addressData.state ?? '')}
                placeholder="Select"
                displayDeselectOption={true}
                size="sm"
                disabled={isLocked}
            />
        );
    };

    const renderEmployerZipField = () => {
        return (
            <TextInput
                name="employerZip"
                displayLabel={true}
                label="Postal Code"
                value={String(addressData.zip ?? '')}
                onChange={(e) => handleInputChange(`${employmentType}.address.zip`, e.target.value)}
                hasError={Boolean(employmentFormErrors.zip.hasError)}
                errorMessage={employmentFormErrors.zip.errorMessage || ''}
                data-testid={`customer-form-${testIdPrefix}-employer-zip`}
                size="small"
                disabled={isLocked}
            />
        );
    };

    return (
        <NameFieldsWrapper columns={4}>
            {renderEmploymentStatus()}
            {renderEmployerNameField()}
            {renderOccupationField()}
            {renderEmployerPhoneField()}
            {employmentType === 'currentEmployment' && renderEmployerAddressField()}
            {employmentType === 'currentEmployment' && renderEmployerCityField()}
            {employmentType === 'currentEmployment' && renderEmployerStateField()}
            {employmentType === 'currentEmployment' && renderEmployerZipField()}
            {employmentType === 'currentEmployment' && renderPayFrequencyField()}
            {employmentType === 'currentEmployment' && renderExpectedIncomeField()}
            <DurationField
                label="Yrs. at Employer"
                value={String(employmentData?.totalYearsEmployed ?? '')}
                field={`${employmentType}.totalYearsEmployed`}
                error={employmentFormErrors.totalYearsEmployed}
            />
            <DurationField
                label="Mos. at Employer"
                value={String(employmentData?.totalMonthsEmployed ?? '')}
                field={`${employmentType}.totalMonthsEmployed`}
                error={employmentFormErrors.totalMonthsEmployed}
            />
        </NameFieldsWrapper>
    );
};
